import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { BookOpen, MapPin, Clock, Sparkles, AlertTriangle, MessageCircle, Flag, QrCode, BookMarked } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { getStoredLocation } from "@/lib/location";
import { distanceKm, modeLabel, EXCHANGE_MODES, type ExchangeMode } from "@/lib/constants";
import { submitReport } from "@/lib/reports.functions";
import { Navbar } from "@/components/Navbar";
import { ReviewsSection } from "@/components/ReviewsSection";
import { TrustBadges } from "@/components/TrustBadges";
import { notifyByEmail } from "@/lib/notify";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { pageHead, jsonLd, SITE_URL } from "@/lib/seo";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/book/$id")({
  loader: async ({ params }) => {
    try {
      const { data } = await supabase
        .from("books")
        .select("title, author, cover_url, description, category")
        .eq("id", params.id)
        .maybeSingle();
      return { book: data ?? null };
    } catch {
      return { book: null };
    }
  },
  head: ({ params, loaderData }) => {
    const b = loaderData?.book as
      | { title: string; author: string; cover_url: string | null; description: string | null; category: string | null }
      | null
      | undefined;
    const title = b ? `${b.title} by ${b.author} — Book Swap` : "Book listing — Book Swap";
    const description = b
      ? (b.description?.trim() ||
          `${b.title} by ${b.author} is available from a neighbor on Book Swap. Borrow, swap or receive it locally — free, no shipping.`
        ).slice(0, 160)
      : "See this book listing on Book Swap and arrange a local swap, loan or donation with the neighbor who owns it.";
    const head = pageHead({ path: `/book/${params.id}`, title, description, type: "article" });
    return {
      ...head,
      meta: b?.cover_url
        ? [...head.meta, { property: "og:image", content: b.cover_url }, { name: "twitter:image", content: b.cover_url }]
        : head.meta,
      scripts: b
        ? [
            jsonLd({
              "@context": "https://schema.org",
              "@type": "Book",
              name: b.title,
              author: { "@type": "Person", name: b.author },
              image: b.cover_url ?? undefined,
              genre: b.category ?? undefined,
              url: `${SITE_URL}/book/${params.id}`,
            }),
          ]
        : [],
    };
  },
  component: BookDetail,
});

type BookDetail = {
  id: string; title: string; author: string; isbn: string | null; cover_url: string | null;
  category: string; age_group: string; exchange_duration: string; condition: string;
  description: string | null; status: string; lat: number | null; lng: number | null; user_id: string;
  exchange_mode?: string | null; is_donation?: boolean | null;
  profiles: { full_name: string | null; avatar_url: string | null; strikes: number | null; verified?: boolean | null; completed_swaps?: number | null; response_rate?: number | null } | null;
};

type PublicProfile = { full_name: string | null; avatar_url: string | null; strikes: number | null; verified?: boolean | null; completed_swaps?: number | null; response_rate?: number | null };

function BookDetail() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [book, setBook] = useState<BookDetail | null>(null);
  const [myBooks, setMyBooks] = useState<{ id: string; title: string }[]>([]);
  const [offered, setOffered] = useState<string>("");
  const [swapChoice, setSwapChoice] = useState<"offer" | "owner">("offer");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportCategory, setReportCategory] = useState<"damage" | "no_return" | "other">("other");
  const [reporting, setReporting] = useState(false);
  const submitReportFn = useServerFn(submitReport);

  async function report() {
    if (!user || !book) return;
    setReporting(true);
    try {
      const res = await submitReportFn({ data: { bookId: book.id, category: reportCategory, reason: reportReason.trim() || null } });
      if (!res.ok) { toast.error(res.error ?? "Could not submit report."); return; }
      toast.success(res.message ?? "Report submitted for review. Thank you.");
      setReportOpen(false);
      setReportReason("");
      setReportCategory("other");
    } catch (e) {
      console.error(e);
      toast.error("Could not submit report.");
    } finally {
      setReporting(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: bookRow, error } = await supabase.from("books")
        .select("*").eq("id", id).maybeSingle();
      if (cancelled) return;
      if (error) { toast.error("Could not load this book."); return; }
      if (!bookRow) { setBook(null); return; }
      const { data: prof } = await supabase
        .from("public_profiles")
        .select("full_name, avatar_url, strikes, verified, completed_swaps, response_rate")
        .eq("id", bookRow.user_id)
        .maybeSingle();
      if (cancelled) return;
      setBook({ ...(bookRow as any), profiles: (prof as PublicProfile) ?? null });
    })();
    if (user) {
      supabase.from("books").select("id,title").eq("user_id", user.id).eq("status", "available")
        .then(({ data }) => { if (!cancelled) setMyBooks(data ?? []); });
    }

    const channel = supabase
      .channel(`book-detail-${id}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "books", filter: `id=eq.${id}` }, (payload) => {
        const newRow: any = payload.new;
        if (!newRow) return;
        setBook((prev) => (prev ? { ...prev, ...newRow } : prev));
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "books", filter: `id=eq.${id}` }, () => {
        setBook(null);
      })
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [id, user]);

  async function sendRequest() {
    if (!user || !book) return;
    const mode = (book.exchange_mode as ExchangeMode) ?? (book.is_donation ? "donate" : "swap");
    const ownerPicks = mode === "swap" && swapChoice === "owner";
    if (mode === "swap" && !ownerPicks && !offered) {
      return toast.error("Pick a book to offer, or let the owner choose from your shelf.");
    }
    if (mode === "swap" && ownerPicks && myBooks.length === 0) {
      return toast.error("List at least one book first so the owner has something to choose from.");
    }
    setSending(true);
    const { data: created, error } = await supabase.from("exchange_requests").insert({
      book_id: book.id,
      requester_id: user.id,
      offered_book_id: mode === "swap" && !ownerPicks ? offered : null,
      owner_picks: ownerPicks,
      message: message || null,
    }).select("id").maybeSingle();
    setSending(false);
    if (error) { console.error(error); return toast.error("Could not send your request."); }
    toast.success("Request sent! The owner will be in touch.");
    if (created?.id) notifyByEmail({ kind: "swap-request", requestId: created.id });
    setMessage(""); setOffered("");
  }

  if (!book) return <div className="min-h-screen bg-background"><Navbar /><p className="p-12 text-center text-muted-foreground">Loading…</p></div>;

  const here = getStoredLocation();
  const dist = here && book.lat != null && book.lng != null ? distanceKm(here.lat, here.lng, book.lat, book.lng) : null;
  const isOwner = user ? book.user_id === user.id : false;
  const mode = ((book.exchange_mode as ExchangeMode) ?? (book.is_donation ? "donate" : "swap")) as ExchangeMode;
  const modeInfo = EXCHANGE_MODES.find((m) => m.value === mode)!;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <Link to="/browse" className="text-sm text-primary hover:underline">← Back to browse</Link>
        <div className="mt-4 grid gap-8 md:grid-cols-[280px_1fr]">
          <div className="aspect-[3/4] overflow-hidden rounded-2xl bg-primary-soft shadow-[var(--shadow-card)]">
            {book.cover_url ? <img src={book.cover_url} alt={book.title} className="h-full w-full object-cover" />
              : <div className="flex h-full items-center justify-center"><BookOpen className="h-16 w-16 text-primary/40" /></div>}
          </div>
          <div>
            <span className="rounded-full bg-primary-soft px-3 py-1 text-xs font-semibold text-primary">{book.category}</span>
            <h1 className="mt-3 text-3xl font-bold">{book.title}</h1>
            <p className="mt-1 text-lg text-muted-foreground">by {book.author}</p>

            <div className="mt-5 flex flex-wrap gap-2">
              <Pill>{modeLabel(mode)}</Pill>
              <Pill>{book.condition}</Pill>
              {mode === "lend" && <Pill><Clock className="mr-1 inline h-3 w-3" />{book.exchange_duration}</Pill>}
              <Pill>{book.age_group}</Pill>
              {dist !== null && <Pill><MapPin className="mr-1 inline h-3 w-3" />{dist.toFixed(1)} km away</Pill>}
            </div>

            {book.description && <p className="mt-5 text-foreground/90">{book.description}</p>}

            <div className="mt-6 rounded-2xl bg-card p-5 shadow-[var(--shadow-card)]">
              <p className="text-sm font-semibold text-primary-dark">Listed by</p>
              <Link to="/u/$id" params={{ id: book.user_id }} className="mt-1 block font-semibold hover:underline">{book.profiles?.full_name ?? "A neighbor"}</Link>
              <div className="mt-3">
                <TrustBadges
                  compact
                  data={{
                    verified: book.profiles?.verified,
                    completed_swaps: book.profiles?.completed_swaps,
                    response_rate: book.profiles?.response_rate,
                    strikes: book.profiles?.strikes,
                  }}
                />
              </div>
              {(book.profiles?.strikes ?? 0) > 0 && (
                <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-destructive/10 px-3 py-1 text-xs font-semibold text-destructive">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {book.profiles!.strikes} {book.profiles!.strikes === 1 ? "strike" : "strikes"} on this account
                </div>
              )}

              {!isOwner && (
                <div className="mt-4 space-y-2">
                  {user ? (
                    <Link to="/messages" search={{ to: book.user_id, book: book.id }}>
                      <Button variant="outline" className="w-full">
                        <MessageCircle className="mr-2 h-4 w-4" /> Message owner
                      </Button>
                    </Link>
                  ) : (
                    <Button variant="outline" className="w-full" onClick={() => navigate({ to: "/login" })}>
                      <MessageCircle className="mr-2 h-4 w-4" /> Sign in to message owner
                    </Button>
                  )}

                  {user && (
                    <Dialog open={reportOpen} onOpenChange={setReportOpen}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="w-full text-muted-foreground hover:text-destructive">
                          <Flag className="mr-2 h-3.5 w-3.5" /> Report listing
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Report this listing</DialogTitle>
                          <DialogDescription>
                            You can only report someone you've completed an exchange with. Every report is reviewed first — nothing is applied instantly.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3">
                          <Select value={reportCategory} onValueChange={(v) => setReportCategory(v as any)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="damage">Book was damaged</SelectItem>
                              <SelectItem value="no_return">Did not return the book</SelectItem>
                              <SelectItem value="other">Other issue</SelectItem>
                            </SelectContent>
                          </Select>
                          <Textarea
                            placeholder="What's the issue? (optional)"
                            maxLength={500}
                            value={reportReason}
                            onChange={(e) => setReportReason(e.target.value)}
                          />
                        </div>
                        <DialogFooter>
                          <Button variant="ghost" onClick={() => setReportOpen(false)} disabled={reporting}>Cancel</Button>
                          <Button onClick={report} disabled={reporting} className="bg-destructive hover:bg-destructive/90">
                            {reporting ? "Submitting…" : "Submit report"}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              )}
            </div>

            {!isOwner && book.status !== "available" && (
              <div className="mt-6 rounded-2xl bg-muted px-4 py-3 text-sm text-muted-foreground">
                This book {book.status === "swapped" || book.status === "exchanged" ? "has already been swapped" : "is not available right now"}. Check back later or browse other titles nearby.
              </div>
            )}

            {!isOwner && book.status === "available" && user && (
              <div className="mt-6 rounded-2xl bg-card p-5 shadow-[var(--shadow-card)]">
                <h2 className="flex items-center gap-2 font-semibold">
                  <Sparkles className="h-4 w-4 text-primary" />
                  {mode === "donate" ? "Request this donation" : mode === "lend" ? "Ask to borrow this book" : "Request this swap"}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">{modeInfo.hint}</p>

                {mode === "swap" && (
                  <div className="mt-4 space-y-3">
                    <p className="text-sm font-semibold">Choose one — this is required for a swap:</p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => setSwapChoice("offer")}
                        aria-pressed={swapChoice === "offer"}
                        className={`rounded-xl border p-3 text-start text-sm transition ${swapChoice === "offer" ? "border-primary bg-primary-soft/50" : "border-border hover:border-primary/50"}`}
                      >
                        <span className="font-semibold">Offer a specific book</span>
                        <span className="mt-1 block text-xs text-muted-foreground">You pick one of your listed books.</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSwapChoice("owner")}
                        aria-pressed={swapChoice === "owner"}
                        className={`rounded-xl border p-3 text-start text-sm transition ${swapChoice === "owner" ? "border-primary bg-primary-soft/50" : "border-border hover:border-primary/50"}`}
                      >
                        <span className="font-semibold">Let the owner choose</span>
                        <span className="mt-1 block text-xs text-muted-foreground">They pick any book from your shelf.</span>
                      </button>
                    </div>

                    {swapChoice === "offer" ? (
                      <Select value={offered} onValueChange={setOffered}>
                        <SelectTrigger><SelectValue placeholder="Pick a book to offer *" /></SelectTrigger>
                        <SelectContent>
                          {myBooks.length === 0 ? <SelectItem value="_" disabled>No books to offer yet</SelectItem>
                            : myBooks.map((b) => <SelectItem key={b.id} value={b.id}>{b.title}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="rounded-xl bg-primary-soft px-3 py-2 text-xs text-primary-dark">
                        The owner will see your {myBooks.length} listed {myBooks.length === 1 ? "book" : "books"} and choose one in return.
                      </p>
                    )}

                    {myBooks.length === 0 && (
                      <Link to="/add-book" className="block text-xs font-semibold text-primary underline">
                        List a book first so you have something to swap
                      </Link>
                    )}
                  </div>
                )}

                <div className="mt-4 space-y-3">
                  <Textarea placeholder="Add a friendly note (optional)" maxLength={300} value={message} onChange={(e) => setMessage(e.target.value)} />
                  <Button onClick={sendRequest} disabled={sending} className="w-full bg-primary hover:bg-primary/90">
                    {sending ? "Sending…" : mode === "donate" ? "Request this book" : mode === "lend" ? "Send borrow request" : "Send swap request"}
                  </Button>
                  <p className="text-xs text-muted-foreground">The owner can view your profile before accepting.</p>
                </div>
              </div>
            )}

            {!isOwner && book.status === "available" && !user && (
              <div className="mt-6 rounded-2xl bg-primary-soft px-4 py-3 text-sm text-primary-dark">
                <Link to="/login" className="font-semibold underline">Sign in</Link> to request this book or message the owner.
              </div>
            )}

            {isOwner && (
              <div className="mt-6 rounded-2xl bg-card p-5 shadow-[var(--shadow-card)]">
                <h2 className="flex items-center gap-2 font-semibold"><QrCode className="h-4 w-4 text-primary" /> QR borrow tag</h2>
                <p className="mt-1 text-sm text-muted-foreground">Print or share this code. Anyone who scans it sends you a borrow request instantly.</p>
                <div className="mt-4 flex justify-center rounded-xl bg-white p-4">
                  <QRCodeSVG value={`${typeof window !== "undefined" ? window.location.origin : ""}/borrow/${book.id}`} size={160} />
                </div>
                <p className="mt-4 rounded-xl bg-primary-soft px-4 py-3 text-sm text-primary-dark">This is your listing. Manage it from My Books.</p>
              </div>
            )}

            {!isOwner && user && (
              <Button variant="outline" className="mt-4 w-full" onClick={async () => {
                const { error } = await supabase.from("reading_journey").upsert({ user_id: user.id, book_id: book.id, status: "want_to_read" }, { onConflict: "user_id,book_id" });
                if (error) toast.error("Could not add."); else toast.success("Added to your reading journey 📖");
              }}>
                <BookMarked className="mr-2 h-4 w-4" /> Add to my journey
              </Button>
            )}
          </div>
        </div>

        <ReviewsSection bookId={book.id} />
      </div>
    </div>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full bg-accent/30 px-3 py-1 text-xs font-medium text-primary-dark">{children}</span>;
}
